const FADE_DURATION = 300;
class CusaToastNotification extends HTMLElement {
	static queue = [];
	static defaultDuration = 6000;
	static queueSpacing = 1500;

	static setDefaults({ duration, spacing }) {
		if (typeof duration === 'number') {
			CusaToastNotification.defaultDuration = duration;
		}
		if (typeof spacing === 'number') {
			CusaToastNotification.queueSpacing = spacing;
		}
	}

	static test(message = 'This is a test toast!', type = 'info', duration = 3000) {
		CusaToastNotification.create(message, type, duration);
	}

	static testQueue(
		toasts = [
			{ message: 'First toast', type: 'info', duration: 3000 },
			{ message: 'Second toast', type: 'success', duration: 3500 },
			{ message: 'Third toast', type: 'warning', duration: 4000 },
			{ message: 'Fourth toast', type: 'error', duration: 3000 },
			{ message: 'Fifth toast', type: 'info', duration: 3000 },
			{ message: 'Sixth toast', type: 'success', duration: 3200 }
		],
		spacing = CusaToastNotification.queueSpacing
	) {
		toasts.forEach((t, i) => {
			setTimeout(() => {
				CusaToastNotification.create(t.message, t.type, t.duration);
			}, i * spacing);
		});
	}

	constructor() {
		super();
		this.toast = null;
		this.messageEl = null;
		this.dismissBtn = null;
		this.hideTimeout = null;
		this.startX = 0;
	}

	connectedCallback() {
		this.toast = this.querySelector('.guardian-toast-notification');
		this.messageEl = this.querySelector('.guardian-toast-message');
		this.dismissBtn = this.querySelector('[data-dismiss-toast]');
		this.dismissBtn?.addEventListener('click', this.hide.bind(this));

		if (this.toast) {
			this.toast.addEventListener(
				'touchstart',
				(e) => {
					this.startX = e.touches[0].clientX;
				},
				{ passive: true }
			);

			this.toast.addEventListener(
				'touchmove',
				(e) => {
					const deltaX = e.touches[0].clientX - this.startX;
					this.toast.style.transform = `translateX(${deltaX}px)`;
					if (Math.abs(deltaX) > 100) {
						this.hide();
					}
				},
				{ passive: true }
			);

			this.toast.addEventListener('touchend', () => {
				this.toast.style.transform = '';
			});
		}
	}

	disconnectedCallback() {
		this.dismissBtn?.removeEventListener('click', this.hide.bind(this));
		// Add cleanup if needed here later — in this case, we're not removing swipe events for simplicity
	}

	show(message = '', type = 'info', duration) {
		if (!this.toast || !this.messageEl) return;
		this.toast.setAttribute('data-type', type);
		this.toast.classList.remove('info', 'success', 'warning', 'error');
		this.toast.classList.add(type);
		this.messageEl.innerHTML = `
      <span class="guardian-toast-icon" aria-hidden="true">
        ${type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️'}
      </span>
      ${message}
    `;
		this.toast.classList.remove('hidden');
		this.toast.classList.add('show', 'guardian-toast-fade');
		clearTimeout(this.hideTimeout);
		const timeout = duration || this.dataset.duration || CusaToastNotification.defaultDuration;
		this.hideTimeout = setTimeout(() => this.hide(), timeout);
	}

	hide() {
		if (!this.toast) return;
		this.toast.classList.remove('show');
		this.toast.classList.add('fade-out');

		setTimeout(() => {
			this.toast.classList.remove('fade-out');
			this.toast.classList.add('hidden');
			this.remove();

			// If there's a queued toast, show it next
			if (CusaToastNotification.queue.length > 0) {
				const next = CusaToastNotification.queue.shift();
				setTimeout(() => {
					CusaToastNotification.create(next.message, next.type, next.duration);
				}, CusaToastNotification.queueSpacing);
			}
		}, FADE_DURATION); // match the CSS animation duration
	}

	static create(message, type = 'info', duration) {
		const container = document.getElementById('guardian-toast-container');
		if (!container) return;

		// Limit the number of toasts to 3, else queue
		const existingToasts = container.querySelectorAll('cusa-toast-notification');
		if (existingToasts.length >= 3) {
			CusaToastNotification.queue.push({ message, type, duration });
			return;
		}

		const toast = document.createElement('cusa-toast-notification');
		if (duration) {
			toast.dataset.duration = duration;
		}
		toast.innerHTML = `
			<div class="guardian-toast-notification ${type} hidden fade-in" role="status" aria-live="polite" data-type="${type}">
      	<button type="button" class="guardian-toast-close" data-dismiss-toast aria-label="Dismiss notification">×</button>
				<p class="guardian-toast-message">
					<span class="guardian-toast-icon" aria-hidden="true">
						${type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️'}
					</span>
					${message}
				</p>
			</div>
		`;
		toast.style.transition = `transform ${FADE_DURATION}ms ease, opacity ${FADE_DURATION}ms ease`;
		toast.style.marginBottom = '1rem';
		container.appendChild(toast);
		toast.show?.(message, type, duration);
	}
}

window.CusaToastNotification = CusaToastNotification;

customElements.define('cusa-toast-notification', CusaToastNotification);
